import React, { useMemo, useState, useEffect } from "react";
import axios from "axios";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Divider from "@mui/material/Divider";
import Collapse from "@mui/material/Collapse";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";

import config from "../../../../config";
import { useRegionView } from "../../../../services/useRegionView";

import VerticalBarChart from "examples/Charts/BarCharts/VerticalBarChart";

// ---- Reusable card: KPI square ----
const StatSquare = ({ title, line1, line2, line3 }) => {
  const hasLine2Or3 = Boolean(line2) || Boolean(line3);
  const isSingleValue = Boolean(line1) && !hasLine2Or3;

  return (
    <Card
      sx={{
        height: { xs: 206, sm: 218, md: 206 },
        borderRadius: 2,
        border: "1px solid rgba(0,0,0,0.12)",
        overflow: "hidden",
      }}
    >
      <MDBox sx={{ height: "100%", p: 2.5, display: "flex", flexDirection: "column" }}>
        <MDTypography variant="button" fontWeight="bold" sx={{ mb: 1 }}>
          {title}
        </MDTypography>

        {/* CONTENT */}
        <MDBox
          sx={{
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: isSingleValue ? "center" : "space-between",
            gap: 1.2,
            minHeight: 0,
          }}
        >
          {/* Mode A: only line1 (positions 1 & 3) */}
          {isSingleValue && (
            <MDTypography
variant="body2" color="text" sx={{ opacity: 1.0 }}
            >
              {line1}
            </MDTypography>
          )}

          {/* Mode B: lines 2 & 3 (positions 2 & 4) */}
          {!isSingleValue && (
            <>
              {!!line1 && (
                <MDTypography variant="body2" color="text" sx={{ opacity: 1.0 }}>
                  {line1}
                </MDTypography>
              )}

              <MDBox sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {!!line2 && (
                  <MDTypography variant="body2" color="text">
                    {line2}
                  </MDTypography>
                )}
                {!!line3 && (
                  <MDTypography variant="body2" color="text">
                    {line3}
                  </MDTypography>
                )}
              </MDBox>
            </>
          )}
        </MDBox>
      </MDBox>
    </Card>
  );
};




/**
 * New Dashboard Template
 * Same structure as GeneralStatistics, but endpoint + mapping are isolated.
 */
const RatioDashboard = () => {
  const API_TOKEN = config.MAP_MATCHING_API_TOKEN;
  const API_URL = config.MAP_MATCHING_API_URL;
  const { island_id } = useRegionView();

 const currentYear = new Date().getFullYear();
const yearOptions = useMemo(() => {
  const years = [];
  for (let y = 2018; y <= currentYear; y += 1) years.push(y);
  return years;
}, [currentYear]);

const [selectedYear, setSelectedYear] = useState(2023);
  const [loading, setLoading] = useState(false);

  // 4 KPI cards (same layout)
  const [stats, setStats] = useState([
    { title: "", line1: "", line2: "", line3: "" },
    { title: "", line1: "", line2: "", line3: "" },
    { title: "", line1: "", line2: "", line3: "" },
    { title: "", line1: "", line2: "", line3: "" },
  ]);

  // Pie diagram cards
  const [pieItems, setPieItems] = useState({
      labels: ["", "", ""],
      datasets: [
        {
          label: "",
          data: [0, 0, 0],
          color: "info",
        }
      ]
    });


  // ---- Centralized fetch (same behavior as your original: first load without filters) ----
  useEffect(() => {
    fetchDashboardData({ withFilters: true });
  }, []);

  const buildParams = ({ withFilters }) => {
    const params = {};

    if (withFilters) {
    if (selectedYear) {
    params.year = selectedYear; // ✅ API expects only year
    }
      if (island_id != null && island_id !== 0) {
        params.island_id = island_id;
      }
    }

    return params;
  };

  // ---- Map backend response -> UI state (edit ONLY here for your new dashboard) ----
  const mapResponseToUI = (data) => {
    // Expecting something like:
    // data.stats: [{title,line1,line2,line3}, ...]
    // data.pieItems: [{title, chart:{labels,datasets}}, ...]
    // If your new endpoint has a different shape, adapt here.

    if (Array.isArray(data?.stats)) setStats(data.stats);
    if (data?.diagram) setPieItems(data.diagram);
  };

  const fetchDashboardData = async ({ withFilters } = { withFilters: true }) => {
    setLoading(true);
    try {
      const params = buildParams({ withFilters });


      const response = await axios.get(`${API_URL}/statistics/ratio`, {
        params,
        headers: {
          "X-API-Key": API_TOKEN,
          "Content-Type": "application/json",
        },
      });

      mapResponseToUI(response.data);
    } catch (e) {
      console.error("Failed to fetch dashboard data", e);
    } finally {
      setLoading(false);
    }
  };

  const handleFetch = async () => {
    if (!selectedYear) return;
    await fetchDashboardData({ withFilters: true });
  };

  return (
    <DashboardLayout>
      <MDBox mt={4} p={0}>
        <Grid container spacing={3}>
          {/* Header + Date Range */}
          <Grid item xs={12}>
            <Card sx={{ overflow: "visible" }}>
              <MDBox p={3}>
                <MDTypography
                  variant="h5"
                  sx={{
                    lineHeight: 1.3,
                    maxWidth: "900px",
                  }}
                >
                  Λόγοι (Εθνικό, Επαρχιακό, Δημοτικό οδικό δίκτυο)
                </MDTypography>
              <MDTypography variant="button" color="text" sx={{ display: "block", mt: 0.5 }}>
                  Πηγή : ΕΛΣΤΑΤ
                </MDTypography>


                <Divider sx={{ my: 2 }} />

                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={6}>
                <MDTypography
                component="label"
                variant="button"
                fontWeight="regular"
                color="text"
                sx={{ mb: 1, mr: 1 }}
                >
                Επιλέξτε χρονιά
                </MDTypography>

                <FormControl size="small" sx={{ minWidth: 150 ,"& .MuiInputBase-root": { height: "2.5rem" }}}>
                <Select
                value={selectedYear}
                onChange={(e) => setSelectedYear(Number(e.target.value))}
                size="medium"
                >
                {yearOptions.map((y) => (
                <MenuItem key={y} value={y}>
                {y}
                </MenuItem>
                ))}
                </Select>
                </FormControl>
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    md={6}
                    sx={{
                      display: "flex",
                      justifyContent: { xs: "flex-start", md: "flex-end" },
                      alignItems: "flex-end",
                    }}
                  >
                    <MDButton
                      variant="gradient"
                      color="info"
                      onClick={handleFetch}
                      disabled={loading || !selectedYear}
                      sx={{ "&.MuiButton-root": { textTransform: "none" } }}
                    >
                      {loading ? "Φόρτωση..." : "Ενημέρωση Στατιστικών"}
                    </MDButton>
                  </Grid>

                  <Grid item xs={12}>
                    <MDBox
                      sx={{
                        borderRadius: 2,
                        p: 2,
                        backgroundColor: "rgba(0,0,0,0.02)",
                      }}
                    >
                      <MDTypography variant="button" color="text" sx={{ opacity: 0.85 }}>
                        Tip: Επίλεξε χρονιά και πάτησε «Ενημέρωση Στατιστικών».
                      </MDTypography>
                    </MDBox>
                  </Grid>
                </Grid>
              </MDBox>
            </Card>
          </Grid>

          {/* KPI blocks (4) */}
          {stats.map((s, idx) => (
            <Grid key={idx} item xs={12} sm={6} md={3}>
              <StatSquare title={s.title} line1={s.line1} line2={s.line2} line3={s.line3} />
            </Grid>
          ))}
        </Grid>
      </MDBox>

      {/* Diagrams section */}
      <Grid item xs={12} sx={{ mt: 4 }}>
        <Card sx={{ overflow: "visible" }}>
          <MDBox p={3}>
            <MDBox
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              flexWrap="wrap"
              gap={2}
            >
              <MDTypography variant="h6"></MDTypography>


            </MDBox>

            <Collapse in={true}>
              <MDBox mt={3}>
                <Grid container spacing={3}>
                <Grid item xs={12}>
                    <MDBox px={{ xs: 1, md: 2 }}>
                    <VerticalBarChart
                        icon={{ color: "dark", component: "leaderboard" }}
                        title="Σύγκριση Δείκτη Θνησιμότητας (Fatality Rate) ανα 1000000 κατ."
                        description="σε Ετήσια Βάση"
                        chart={pieItems}
                    />
                    </MDBox>
                </Grid>
                </Grid>
              </MDBox>
            </Collapse>
          </MDBox>
        </Card>
      </Grid>
    </DashboardLayout>
  );
};

export default RatioDashboard;
